import { FormattedMessage, useIntl } from 'react-intl';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import type { DeletionImpact } from './useFindingDeletion';
import type { RecordType } from './deletionPlan';

/** How many descriptions the dialog names before it only counts. */
const LISTED_MAX = 8;

/** The order the counted record types are named in. */
const RECORD_ORDER: RecordType[] = [
    'photo',
    'drawingSheet',
    'find',
    'sample',
    'overallDescription'
];

/**
 * The confirm dialog's account of what a deletion takes with it — the
 * descriptions of a Befund, the pictures and sketches attached, the other
 * descriptions losing a reference (named) and the records losing their link
 * (counted).
 * Nothing when the deletion touches nothing else.
 */
export default function DeletionImpactDetails({
    impact
}: {
    impact: DeletionImpact | null;
}) {
    const intl = useIntl();
    if (!impact) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <FormattedMessage id="finding.delete.searching" />
            </Typography>
        );
    }
    const cascadedMedia = impact.cascadedPhotos + impact.cascadedSketches;
    const recordCounts = RECORD_ORDER.filter(
        (type) => impact.records[type] > 0
    ).map((type) =>
        intl.formatMessage(
            { id: `finding.delete.count.${type}` },
            { count: impact.records[type] }
        )
    );
    if (
        impact.cascadedDescriptions === 0 &&
        cascadedMedia === 0 &&
        impact.movedMedia === 0 &&
        impact.descriptions.length === 0 &&
        recordCounts.length === 0
    ) {
        return null;
    }
    const listed = impact.descriptions.slice(0, LISTED_MAX);
    const hidden = impact.descriptions.length - listed.length;
    return (
        <Alert severity="warning" sx={{ mt: 2 }}>
            {impact.cascadedDescriptions > 0 && (
                <Typography variant="body2">
                    <FormattedMessage
                        id="finding.delete.cascade"
                        values={{ count: impact.cascadedDescriptions }}
                    />
                </Typography>
            )}
            {cascadedMedia > 0 && (
                <Typography variant="body2">
                    <FormattedMessage
                        id="finding.delete.media"
                        values={{
                            photos: impact.cascadedPhotos,
                            sketches: impact.cascadedSketches
                        }}
                    />
                </Typography>
            )}
            {impact.movedMedia > 0 && (
                <Typography variant="body2">
                    <FormattedMessage
                        id="finding.delete.mediaMoved"
                        values={{ count: impact.movedMedia }}
                    />
                </Typography>
            )}
            {impact.descriptions.length > 0 && (
                <>
                    <Typography variant="body2">
                        <FormattedMessage
                            id="finding.delete.references"
                            values={{ count: impact.descriptions.length }}
                        />
                    </Typography>
                    <List dense disablePadding>
                        {listed.map((item) => (
                            <ListItem key={item.key} disableGutters>
                                <ListItemText
                                    primary={item.label}
                                    secondary={item.detail}
                                />
                            </ListItem>
                        ))}
                    </List>
                    {hidden > 0 && (
                        <Typography variant="body2">
                            <FormattedMessage
                                id="finding.delete.more"
                                values={{ count: hidden }}
                            />
                        </Typography>
                    )}
                </>
            )}
            {recordCounts.length > 0 && (
                <Typography variant="body2">
                    <FormattedMessage
                        id="finding.delete.records"
                        values={{
                            list: intl.formatList(recordCounts, {
                                type: 'conjunction'
                            })
                        }}
                    />
                </Typography>
            )}
        </Alert>
    );
}
